import React from 'react';
import { ReactSortable } from 'react-sortablejs';
import {
  Box,
  Chip,
  Collapse,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  useFetchCampaignSteps,
  useReferenceObjectAdmin,
} from '../../../../hooks/campaign-hooks';
import { Campaign, CampaignCustomerPersona, PersonaRole } from '../../../../support/campaigns';
import { personaSelectionRef } from '../../../Campaigns/workshopPrepConfig';
import { GhostButton, SectionHeader, builderStyles } from './primitives';
import { byOrder, nextOrderOf, signature } from './builderShared';
import { buildReferenceUsage, WhereUsed } from './referenceUsage';

/**
 * Admin editor for the customer-side personas a campaign's workshop wants in the room
 * (`CampaignCustomerPersona`) — the data-driven replacement for the guided runner's static
 * `STAKEHOLDERS` list. `role` is priority advice to the champion (must/should/nice), not a hard gate;
 * `why` is the one-line reason the persona matters.
 *
 * Rows are not edited inline: each shows a read-only summary and an Edit control that expands its
 * config plus a "where it's used" section listing the steps (prep sections) that select this persona.
 * Same full-record PUT persistence as the rest of the builder; drag order persists to `order`.
 */

const PERSONA_ROLES: { value: PersonaRole; label: string }[] = [
  { value: 'must', label: 'Must have' },
  { value: 'should', label: 'Should have' },
  { value: 'nice', label: 'Nice to have' },
];

export function PersonasEditor(props: { campaign: Campaign | null; canWrite: boolean }): JSX.Element {
  const { campaign, canWrite } = props;

  // Global (library) mode = no campaign: manage every global persona directly (create/edit/delete).
  // Campaign mode = a campaign references a subset: `admin.linked` is that subset, `admin.unlinked`
  // backs the "attach existing" picker, and `createLinked`/`detach` create-and-link / detach without
  // deleting the (possibly shared) global persona.
  const global = campaign == null;
  const admin = useReferenceObjectAdmin<CampaignCustomerPersona>('persona', campaign?.id);
  const rows = global ? admin.all : admin.linked;
  const isLoading = admin.isLoading;
  const { data: steps = [] } = useFetchCampaignSteps(campaign?.id);
  const mut = admin;

  const [personas, setPersonas] = React.useState<CampaignCustomerPersona[]>([]);
  const sig = signature(rows);
  React.useEffect(() => {
    setPersonas(byOrder(rows));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  const [attachAnchor, setAttachAnchor] = React.useState<null | HTMLElement>(null);

  const [expanded, setExpanded] = React.useState<Set<number>>(new Set());
  const toggleExpanded = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const stepsSig = signature(steps);
  const usage = React.useMemo(
    () => buildReferenceUsage(steps),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stepsSig],
  );
  const roleLabel = (r: PersonaRole) => PERSONA_ROLES.find((x) => x.value === r)?.label ?? r;

  const patchLocal = (id: number, next: Partial<CampaignCustomerPersona>) =>
    setPersonas((prev) => prev.map((p) => (p.id === id ? { ...p, ...next } : p)));

  const save = (row: CampaignCustomerPersona, next: Partial<CampaignCustomerPersona>) => {
    patchLocal(row.id, next);
    mut.update.mutate({ ...row, ...next, id: row.id });
  };

  const add = () => {
    const fields = {
      label: '',
      role: 'should' as PersonaRole,
      why: '',
      order: nextOrderOf(personas),
      hidden: false,
    };
    // Global mode creates a bare global persona; campaign mode creates-and-links it to the campaign.
    if (global) void admin.create(fields);
    else void admin.createLinked(fields);
  };

  const attach = (id: number) => admin.attachExisting(id);

  // Campaign mode: "Remove" detaches this campaign's reference; the global persona (which another
  // campaign may reference) is left intact. Global mode: delete the global persona everywhere (with
  // confirmation, since it is removed from every campaign that references it).
  const remove = (row: CampaignCustomerPersona) => {
    if (global) {
      const ok = window.confirm(
        `Delete “${row.label.trim() || 'this persona'}” from the global library? It will be removed from every campaign that references it.`,
      );
      if (!ok) return;
      setPersonas((prev) => prev.filter((p) => p.id !== row.id));
      admin.removeGlobal(row.id);
      return;
    }
    setPersonas((prev) => prev.filter((p) => p.id !== row.id));
    admin.detach(row.id);
  };

  const reorder = (nextList: CampaignCustomerPersona[]) => {
    setPersonas(nextList);
    nextList.forEach((row, index) => {
      if ((row.order ?? 0) !== index) mut.update.mutate({ ...row, order: index, id: row.id });
    });
  };

  return (
    <div className={builderStyles.contentCard}>
      <SectionHeader
        compact
        title="Customer personas"
        subtitle="The customer-side people the workshop wants in the room. Priority is advice to the champion, not a gate."
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <>
          {personas.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No personas yet. Add the roles this workshop needs represented.
            </Typography>
          )}

          <ReactSortable
            list={personas.map((p) => ({ ...p }))}
            setList={(next) => canWrite && reorder(next as CampaignCustomerPersona[])}
            handle=".cb-persona-drag-handle"
            animation={150}
            disabled={!canWrite}
          >
            {personas.map((row) => {
              const isOpen = expanded.has(row.id);
              const usedIn = usage.personas.get(personaSelectionRef(row)) ?? [];
              return (
                <Box
                  key={row.id}
                  sx={{
                    border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 1,
                    p: 1,
                    mb: 1,
                    opacity: row.hidden ? 0.55 : 1,
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Box
                      className="cb-persona-drag-handle"
                      sx={{ cursor: canWrite ? 'grab' : 'default', color: 'text.disabled', pt: 0.25, display: 'flex' }}
                    >
                      <DragIndicatorIcon fontSize="small" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.label.trim() || <em>(untitled persona)</em>}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }} useFlexGap flexWrap="wrap">
                        <Chip size="small" variant="outlined" label={roleLabel(row.role)} />
                        {global ? (
                          <Typography variant="caption" color="text.secondary">
                            · used by {admin.usageCount(row.id)} campaign
                            {admin.usageCount(row.id) === 1 ? '' : 's'}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            · used in {usedIn.length} step{usedIn.length === 1 ? '' : 's'}
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    {canWrite && (
                      <Stack direction="row">
                        <Tooltip title={isOpen ? 'Close' : 'Edit'}>
                          <IconButton size="small" onClick={() => toggleExpanded(row.id)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={row.hidden ? 'Hidden from the runner — show' : 'Hide from the runner'}>
                          <IconButton size="small" onClick={() => save(row, { hidden: !row.hidden })}>
                            {row.hidden ? (
                              <VisibilityOffIcon fontSize="small" />
                            ) : (
                              <VisibilityIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={global ? 'Delete persona (removes it from all campaigns)' : 'Remove persona'}>
                          <IconButton size="small" onClick={() => remove(row)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </Stack>

                  <Collapse in={isOpen} unmountOnExit>
                    <Stack spacing={1} sx={{ mt: 1.25 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField
                          label="Persona"
                          size="small"
                          fullWidth
                          value={row.label}
                          disabled={!canWrite}
                          onChange={(e) => patchLocal(row.id, { label: e.target.value })}
                          onBlur={() => save(row, { label: row.label })}
                        />
                        <TextField
                          select
                          label="Priority"
                          size="small"
                          value={row.role}
                          disabled={!canWrite}
                          onChange={(e) => save(row, { role: e.target.value as PersonaRole })}
                          sx={{ minWidth: 160 }}
                        >
                          {PERSONA_ROLES.map((r) => (
                            <MenuItem key={r.value} value={r.value}>
                              {r.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                      <TextField
                        label="Why they matter"
                        size="small"
                        fullWidth
                        multiline
                        value={row.why}
                        disabled={!canWrite}
                        onChange={(e) => patchLocal(row.id, { why: e.target.value })}
                        onBlur={() => save(row, { why: row.why })}
                      />
                      {!global && <WhereUsed steps={usedIn} noun="persona" />}
                    </Stack>
                  </Collapse>
                </Box>
              );
            })}
          </ReactSortable>

          {canWrite && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <GhostButton onClick={add} disabled={mut.isSaving}>
                + Add persona
              </GhostButton>
              {/* "Attach existing" only makes sense inside a campaign; the global library shows all. */}
              {!global && (
                <>
                  <GhostButton
                    onClick={(e) => setAttachAnchor(e.currentTarget)}
                    disabled={mut.isSaving || admin.unlinked.length === 0}
                  >
                    Add existing
                  </GhostButton>
                  <Menu
                    anchorEl={attachAnchor}
                    open={Boolean(attachAnchor)}
                    onClose={() => setAttachAnchor(null)}
                  >
                    {admin.unlinked.map((p) => (
                      <MenuItem
                        key={p.id}
                        onClick={() => {
                          attach(p.id);
                          setAttachAnchor(null);
                        }}
                      >
                        {p.label.trim() || `(untitled persona #${p.id})`}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Stack>
          )}
        </>
      )}
    </div>
  );
}

export default PersonasEditor;
